import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { Dialog } from "@repo/ui";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Link2,
  Loader2,
  Mail,
  Plus,
  Search,
} from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { buildParcoursDescription } from "@repo/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

type FiltreStatut = "tous" | "en_cours" | "termine" | "archive";

const PAGE_SIZE = 10;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const FiltresParcours = memo(function FiltresParcours({
  filtreCandidatInput,
  setFiltreCandidatInput,
  filtreStatut,
  setFiltreStatut,
  filtreType,
  setFiltreType,
  filtreCampagne,
  setFiltreCampagne,
  typesParcours,
  campagnes,
  campagneNomFromUrl,
  onReset,
}: {
  filtreCandidatInput: string;
  setFiltreCandidatInput: (value: string) => void;
  filtreStatut: FiltreStatut;
  setFiltreStatut: (value: FiltreStatut) => void;
  filtreType: string;
  setFiltreType: (value: string) => void;
  filtreCampagne: string;
  setFiltreCampagne: (value: string) => void;
  typesParcours: string[];
  campagnes: { _id: string; nom: string }[];
  campagneNomFromUrl?: string;
  onReset: () => void;
}) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Filtres</CardTitle>
        <CardDescription>Recherchez et filtrez les parcours</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Nom, prénom ou email..."
              value={filtreCandidatInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFiltreCandidatInput(e.target.value);
              }}
              className="pl-9"
            />
          </div>

          <Select
            value={filtreStatut}
            onValueChange={(value: FiltreStatut) => {
              setFiltreStatut(value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous les statuts</SelectItem>
              <SelectItem value="en_cours">En cours</SelectItem>
              <SelectItem value="termine">Terminés</SelectItem>
              <SelectItem value="archive">Archivés</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filtreType}
            onValueChange={(value: string) => {
              setFiltreType(value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Type de parcours" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous les types</SelectItem>
              {typesParcours.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filtreCampagne}
            onValueChange={(value: string) => {
              setFiltreCampagne(value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Campagne" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Toutes les campagnes</SelectItem>
              {campagnes.map((c) => (
                <SelectItem key={c._id} value={c._id}>
                  {c.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={onReset}>
            Réinitialiser
          </Button>
        </div>
        {campagneNomFromUrl && (
          <div className="mt-3">
            <Badge variant="outline" className="text-sm">
              Campagne : {campagneNomFromUrl}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export function ParcoursList() {
  const [searchParams] = useSearchParams();
  const campagneFromUrl = searchParams.get("campagne");

  const [page, setPage] = useState(1);
  const [filtreStatut, setFiltreStatut] = useState<FiltreStatut>("tous");
  const [filtreCandidatInput, setFiltreCandidatInput] = useState("");
  const [filtreType, setFiltreType] = useState<string>("tous");
  const [filtreCampagne, setFiltreCampagne] = useState<string>(
    campagneFromUrl || "tous",
  );
  const [rattacherModal, setRattacherModal] =
    useState<Id<"parcoursEvaluations"> | null>(null);
  const [detacherModal, setDetacherModal] =
    useState<Id<"parcoursEvaluations"> | null>(null);
  const [selectedCampagne, setSelectedCampagne] = useState<string>("");
  const [errorModal, setErrorModal] = useState<string | null>(null);

  const filtreCandidatDebounced = useDebouncedValue(filtreCandidatInput, 300);

  const archiverParcours = useMutation(
    api.parcoursEvaluations.archiverParcours,
  );
  const desarchiverParcours = useMutation(
    api.parcoursEvaluations.desarchiverParcours,
  );
  const rattacherParcours = useMutation(api.campagnes.rattacherParcours);
  const detacherParcours = useMutation(api.campagnes.detacherParcours);
  const envoyerLienParcours = useAction(api.email.envoyerLienParcours);

  const campagnes = useQuery(api.campagnes.listerToutesCampagnes) ?? [];
  const campagnesCompatibles = useQuery(
    api.campagnes.obtenirCampagnesCompatibles,
    rattacherModal ? { parcoursId: rattacherModal } : "skip",
  );

  const campagneNomFromUrl = useMemo(() => {
    if (!campagneFromUrl) return undefined;
    return campagnes.find((c) => c._id === campagneFromUrl)?.nom;
  }, [campagneFromUrl, campagnes]);

  const result = useQuery(api.parcoursEvaluations.listerParcoursAdmin, {
    page,
    pageSize: PAGE_SIZE,
    filtreStatut,
    filtreCandidat: filtreCandidatDebounced || undefined,
    filtreType: filtreType === "tous" ? undefined : filtreType,
    filtreCampagneId:
      filtreCampagne !== "tous"
        ? (filtreCampagne as Id<"campagnes">)
        : undefined,
  });

  const handleArchive = async (parcoursId: Id<"parcoursEvaluations">) => {
    await archiverParcours({ parcoursId });
  };

  const handleDesarchive = async (parcoursId: Id<"parcoursEvaluations">) => {
    await desarchiverParcours({ parcoursId });
  };

  const handleSendEmail = async (
    parcoursId: Id<"parcoursEvaluations">,
    email: string,
  ) => {
    const res = await envoyerLienParcours({ parcoursId });
    if (res.succes) {
      alert(`Lien envoyé à ${email}\n\nLien: ${res.lien}`);
    } else {
      alert(`Erreur: ${res.message}`);
    }
  };

  const handleRattacher = async () => {
    if (!rattacherModal || !selectedCampagne) return;
    try {
      await rattacherParcours({
        parcoursId: rattacherModal,
        campagneId: selectedCampagne as Id<"campagnes">,
      });
      setRattacherModal(null);
      setSelectedCampagne("");
    } catch (e) {
      const msg = e instanceof Error
        ? (e.message.match(/Uncaught Error: (.+)/)?.[1] ?? e.message)
        : "Impossible de rattacher le parcours";
      setErrorModal(msg);
    }
  };

  const handleDetacher = async () => {
    if (!detacherModal) return;
    try {
      await detacherParcours({ parcoursId: detacherModal });
      setDetacherModal(null);
    } catch (e) {
      const msg = e instanceof Error
        ? (e.message.match(/Uncaught Error: (.+)/)?.[1] ?? e.message)
        : "Impossible de détacher le parcours";
      setErrorModal(msg);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getProgressionBadge = (pourcentage: number, enCours: number) => {
    if (pourcentage === 100) {
      return <Badge variant="success">Terminé</Badge>;
    } else if (pourcentage > 0 || enCours > 0) {
      return <Badge variant="warning">En cours</Badge>;
    }
    return <Badge variant="secondary">Non commencé</Badge>;
  };

  const typesParcours = useMemo(() => {
    if (!result) return [];
    const types = new Set(result.parcours.map((p) => p.typeParcours));
    return Array.from(types);
  }, [result]);

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { parcours, total, totalPages } = result;

  return (
    <div className="min-h-screen flex flex-col items-start">
      <div className="w-full container mx-auto p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              {filtreCampagne !== "tous" && campagneNomFromUrl
                ? campagneNomFromUrl
                : "Parcours d'évaluation"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {total} parcours au total
            </p>
          </div>
          <Link to="/creer">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Créer un parcours
            </Button>
          </Link>
        </div>

        <FiltresParcours
          filtreCandidatInput={filtreCandidatInput}
          setFiltreCandidatInput={setFiltreCandidatInput}
          filtreStatut={filtreStatut}
          setFiltreStatut={setFiltreStatut}
          filtreType={filtreType}
          setFiltreType={setFiltreType}
          filtreCampagne={filtreCampagne}
          setFiltreCampagne={setFiltreCampagne}
          typesParcours={typesParcours}
          campagnes={campagnes}
          campagneNomFromUrl={campagneNomFromUrl}
          onReset={() => {
            setFiltreCandidatInput("");
            setFiltreStatut("tous");
            setFiltreType("tous");
            setFiltreCampagne("tous");
            setPage(1);
          }}
        />

        <Card>
          <CardContent className="p-0">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[16%]">Candidat</TableHead>
                  <TableHead className="w-[16%]">Email</TableHead>
                  <TableHead className="w-[10%]">Type</TableHead>
                  <TableHead className="w-[13%]">Progression</TableHead>
                  <TableHead className="w-[11%]">Date création</TableHead>
                  <TableHead className="w-[9%]">Statut</TableHead>
                  <TableHead className="w-[9%]">Campagne</TableHead>
                  <TableHead className="w-[16%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcours.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <p className="text-muted-foreground">
                        Aucun parcours trouvé
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  parcours.map((p) => (
                    <TableRow
                      key={p._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => (window.location.href = `/p/${p._id}`)}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {p.prenomCandidat} {p.nomCandidat}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {p.titre}
                          </div>
                          <div className="text-xs text-muted-foreground/70">
                            {buildParcoursDescription(p.evaluationsResume)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{p.emailCandidat}</TableCell>
                      <TableCell className="max-w-37.5">
                        <Badge variant="outline" className="max-w-full overflow-hidden" title={p.typeParcours}>
                          <span className="truncate">{p.typeParcours}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="min-w-[120px]">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span>{p.progression.pourcentage}%</span>
                            <span className="text-muted-foreground">
                              {p.progression.terminees}/{p.progression.total}
                            </span>
                          </div>
                          <Progress
                            value={p.progression.pourcentage}
                            className="h-2"
                          />
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(p.dateCreation)}</TableCell>
                      <TableCell>
                        {p.archive ? (
                          <Badge variant="secondary">Archivé</Badge>
                        ) : (
                          getProgressionBadge(p.progression.pourcentage, p.progression.enCours)
                        )}
                      </TableCell>
                      <TableCell>
                        {p.campagneId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setDetacherModal(p._id);
                            }}
                            title="Détacher de la campagne"
                          >
                            <Link2 className="h-3 w-3 mr-1" />
                            Lié
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-muted-foreground"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setRattacherModal(p._id);
                              setSelectedCampagne("");
                            }}
                            title="Rattacher à une campagne"
                          >
                            <Link2 className="h-3 w-3 mr-1" />
                            Rattacher
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              window.open(
                                `${process.env.BUN_PUBLIC_APP_CANDIDATE_PORTAIL_URL}/p/${p._id}`,
                                "_blank",
                              );
                            }}
                            title="Voir le portail candidat"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleSendEmail(p._id, p.emailCandidat);
                            }}
                            title="Envoyer le lien par email"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          {p.archive ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleDesarchive(p._id);
                              }}
                              title="Désarchiver"
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleArchive(p._id);
                              }}
                              title="Archiver"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="icon"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} sur {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <Dialog
        isOpen={rattacherModal !== null}
        onClose={() => {
          setRattacherModal(null);
          setSelectedCampagne("");
        }}
        title="Rattacher à une campagne"
        className="max-w-md"
      >
        <div className="space-y-4">
          {campagnesCompatibles === undefined ? (
            <p className="text-sm text-muted-foreground">Chargement...</p>
          ) : campagnesCompatibles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucune campagne compatible avec ce parcours.
            </p>
          ) : (
            <Select value={selectedCampagne} onValueChange={setSelectedCampagne}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une campagne" />
              </SelectTrigger>
              <SelectContent>
                {campagnesCompatibles.map((c) => (
                  <SelectItem key={c._id} value={c._id}>
                    {c.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setRattacherModal(null);
                setSelectedCampagne("");
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleRattacher}
              disabled={!selectedCampagne || campagnesCompatibles?.length === 0}
            >
              Rattacher
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={detacherModal !== null}
        onClose={() => setDetacherModal(null)}
        title="Détacher de la campagne"
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Voulez-vous détacher ce parcours de sa campagne ? Cette action est
            réversible via le bouton "Rattacher".
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDetacherModal(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDetacher}>
              Détacher
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={errorModal !== null}
        onClose={() => setErrorModal(null)}
        title="Erreur"
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{errorModal}</p>
          <div className="flex justify-end">
            <Button onClick={() => setErrorModal(null)}>Fermer</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
